import { EmbedSignDocument } from "@documenso/embed-react";
import { DocumentTextIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { t } from "@lingui/core/macro";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { cn } from "@/lib/utils";
import { selectMaybeActiveAccountEid } from "@/redux/app/appSelectors";
import {
  useCompleteProductWithdrawalMutation,
  useGetProductWithdrawalDocMutation,
} from "@/routes/_authenticated/trade/-api/trade";
import type { WithdrawalVerification } from "@/shared/api/types/api";
import { CircularProgress } from "@/shared/components/ui/circular-progress";
import { resolveProductLines } from "@/util/withdrawal";

import { SelectedProductsTable } from "../../-components/Withdrawal/SelectedProductsTable";
import {
  HelpBlock,
  Screen,
  StepFooter,
  SuccessDialog,
  useFlow,
  useFlowNavigate,
} from "../../-components/Withdrawal/WithdrawalFlow";
import { FinalCheck, SubmitErrorBanner, submitErrorMessage } from "../../-components/Withdrawal/WithdrawalReview";

const DOCUMENSO_HOST = import.meta.env.VITE_DOCUMENSO_HOST;

const DocumentLoadingState = ({ documentCreated }: { documentCreated: boolean }) => (
  <div
    className="flex h-[60vh] max-h-[600px] min-h-[480px] items-center justify-center bg-muted/10 px-6 py-12"
    role="status"
    aria-live="polite"
  >
    <div className="flex max-w-sm flex-col items-center text-center">
      <div className="relative flex h-14 w-14 items-center justify-center rounded-xl border border-primary/15 bg-background shadow-sm">
        <DocumentTextIcon className="h-7 w-7 text-primary" aria-hidden />
        <CircularProgress className="absolute -right-2 -bottom-2 h-5 w-5 rounded-full bg-background" />
      </div>
      <p className="mt-5 text-sm font-semibold">
        {documentCreated ? t`Loading secure signing` : t`Preparing your withdrawal document`}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        {documentCreated
          ? t`Your document is ready. The signing form is loading now.`
          : t`We are creating the document with your withdrawal details.`}
      </p>
      <p className="mt-3 text-xs text-muted-foreground">{t`This can take a few seconds. Keep this page open.`}</p>
    </div>
  </div>
);

/** Address changes require confirmation before another document can be created. */
const documentErrorMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== "object" || !("status" in error)) return undefined;
  if (error.status === 422)
    return t`Your account has no address on file. Go back to the shipping step and enter an address.`;
  if (error.status === 409) return t`Your address on file changed. Go back to the shipping step and confirm it again.`;
  return undefined;
};

const DocumentErrorState = ({ message }: { message?: string }) => (
  <div className="withdrawal-document-error flex h-[60vh] max-h-[600px] min-h-[480px] items-center justify-center bg-muted/10 px-6 py-12">
    <div className="flex max-w-sm flex-col items-center text-center" role="alert">
      <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-destructive/20 bg-background shadow-sm">
        <ExclamationTriangleIcon className="h-7 w-7 text-destructive" aria-hidden />
      </div>
      <p className="mt-5 text-sm font-semibold">{t`We could not prepare your document`}</p>
      <p className="mt-1 text-sm text-muted-foreground">
        {message ?? t`Try again later or contact Client Relations if the problem continues.`}
      </p>
    </div>
  </div>
);

const WithdrawalDocumentPanel = ({
  document,
  createFailed,
  createErrorMessage,
  signed,
  onCompleted,
}: {
  document?: { token: string; documentId: string };
  createFailed: boolean;
  createErrorMessage?: string;
  signed: boolean;
  onCompleted: () => void;
}) => {
  const [readyToken, setReadyToken] = useState<string | null>(null);
  const documentReady = document !== undefined && readyToken === document.token;

  const documentBody = () => {
    if (createFailed) {
      return <DocumentErrorState message={createErrorMessage} />;
    }
    if (signed) {
      return (
        <p className="flex items-center gap-2 p-5 text-sm">
          <CheckCircleIcon className="h-5 w-5 text-primary" aria-hidden />
          {t`Document signed. Complete the final check below to submit your request.`}
        </p>
      );
    }
    if (!document) {
      return <DocumentLoadingState documentCreated={false} />;
    }
    return (
      <div className="relative min-h-[480px] [&_iframe]:h-[60vh] [&_iframe]:max-h-[600px] [&_iframe]:min-h-[480px]">
        {!documentReady && (
          <div className="absolute inset-0 z-10">
            <DocumentLoadingState documentCreated />
          </div>
        )}
        <EmbedSignDocument
          className={cn("withdrawal-document-frame w-full", documentReady && "is-ready")}
          token={document.token}
          host={DOCUMENSO_HOST}
          onDocumentReady={() => setReadyToken(document.token)}
          onDocumentCompleted={onCompleted}
        />
      </div>
    );
  };

  return (
    <section className="overflow-hidden rounded-lg border" aria-labelledby="withdrawal-document-title">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-muted/50 px-5 py-3">
        <h3 id="withdrawal-document-title" className="text-sm font-semibold">
          {t`Withdrawal authorization`}
        </h3>
      </div>
      {documentBody()}
    </section>
  );
};

const ProductSignatureScreen = () => {
  const navigate = useFlowNavigate();
  const { inputs, productLines, shipping, reset } = useFlow();
  const eid = useSelector(selectMaybeActiveAccountEid);
  const [createDocument, { data: document, isUninitialized, isError: createFailed, error: createError }] =
    useGetProductWithdrawalDocMutation();
  const [completeWithdrawal, { isLoading: submitting }] = useCompleteProductWithdrawalMutation();
  const [signed, setSigned] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submissionId = useRef(crypto.randomUUID()).current;
  const lines = resolveProductLines(productLines, inputs.products);

  useEffect(() => {
    if (!isUninitialized || lines.length === 0 || !eid || !shipping) return;
    createDocument({
      eid,
      shipping,
      lines: lines.map(({ metal, name, unitOz, quantity }) => ({ metal, name, unitOz, quantity })),
    });
  }, [isUninitialized, lines, eid, shipping, createDocument]);

  if (lines.length === 0) return <Navigate to="/trade/withdraw/products" replace />;

  if (!shipping) return <Navigate to="/trade/withdraw/products/shipping" replace />;

  const submit = async (verification: WithdrawalVerification) => {
    if (!eid || !document) return;
    setSubmitError(null);
    try {
      await completeWithdrawal({
        eid,
        documentId: document.documentId,
        shipping,
        lines: lines.map(({ metal, name, unitOz, quantity }) => ({ metal, name, unitOz, quantity })),
        submissionId,
        ...verification,
      }).unwrap();
      setSubmitted(true);
    } catch (error) {
      setSubmitError(submitErrorMessage(error));
      throw error;
    }
  };

  const finalReviewLines = [{ label: t`Shipping address`, value: shipping.address }];
  return (
    <Screen
      subtitle={t`Sign your withdrawal document, then verify to submit your request.`}
      step={{ index: 5, total: 5 }}
    >
      <WithdrawalDocumentPanel
        document={document}
        createFailed={createFailed}
        createErrorMessage={documentErrorMessage(createError)}
        signed={signed}
        onCompleted={() => setSigned(true)}
      />
      {signed ? (
        <FinalCheck
          details={<SelectedProductsTable lines={lines} variant="compact" />}
          lines={finalReviewLines}
          onSubmit={submit}
          onBack={() => navigate("products/review")}
          submitting={submitting}
        >
          {submitError && <SubmitErrorBanner message={submitError} />}
        </FinalCheck>
      ) : (
        <StepFooter onBack={() => navigate("products/review")} />
      )}
      <HelpBlock />
      <SuccessDialog
        open={submitted}
        headline={t`Your withdrawal is in process`}
        note={t`Thank you for your submission, a member of our customer support team will reach out from accounts@monetary-metals.com to provide you with a quote for the products selected. You have the opportunity to review and finalize those details before your products are sent.`}
        details={<SelectedProductsTable lines={lines} className="mb-2" variant="compact" />}
        lines={finalReviewLines}
        onDone={() => {
          reset();
          navigate("");
        }}
      />
    </Screen>
  );
};

export const Route = createFileRoute("/_authenticated/trade/withdraw/products/signature")({
  component: ProductSignatureScreen,
});
